import { Search, GhostButton, Picker } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import {
  useActivityLogStatusPickerItems,
  useRunResultPickerItems,
} from "../monitoringEnums";
import type { EnrollmentDetailCheck } from "../types";
import { ActivityLogDateFilter } from "./ActivityLogDateFilter";

export interface ActivityLogFilterState {
  search: string;
  checkTypes: string[];
  monitorStatuses: string[];
  resultStatuses: string[];
  dateRange: string | null;
}

interface Props {
  filters: ActivityLogFilterState;
  checkOptions: EnrollmentDetailCheck[];
  onChange: (next: ActivityLogFilterState) => void;
  onResetPage: () => void;
}

export function ActivityLogFilters({
  filters,
  checkOptions,
  onChange,
  onResetPage,
}: Props) {
  const { t } = useTranslation();
  const runStatusOptions = useActivityLogStatusPickerItems();
  const runResultOptions = useRunResultPickerItems();

  const checkTypeItems = checkOptions.map((enrollment) => ({
    id: enrollment.check_type,
    name: enrollment.check_label,
  }));

  const hasActiveFilters = Boolean(
    filters.search.trim() ||
      filters.checkTypes.length ||
      filters.monitorStatuses.length ||
      filters.resultStatuses.length ||
      filters.dateRange
  );

  function update(partial: Partial<ActivityLogFilterState>) {
    onChange({ ...filters, ...partial });
    onResetPage();
  }

  function clearFilters() {
    onChange({
      search: "",
      checkTypes: [],
      monitorStatuses: [],
      resultStatuses: [],
      dateRange: null,
    });
    onResetPage();
  }

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <ActivityLogDateFilter
          dateRange={filters.dateRange}
          onChange={(dateRange) => update({ dateRange })}
        />
        {checkTypeItems.length > 0 && (
          <Picker
            label={t("Check")}
            showActionBar
            selectionMode="multiple"
            selectedKeys={filters.checkTypes}
            onSelectionChange={(keys) =>
              update({ checkTypes: Array.from(keys).map(String) })
            }
            items={checkTypeItems}
            testID="activity-log-check-filter"
          />
        )}
        <Picker
          label={t("Status")}
          showActionBar
          selectionMode="multiple"
          selectedKeys={filters.monitorStatuses}
          onSelectionChange={(keys) =>
            update({ monitorStatuses: Array.from(keys).map(String) })
          }
          items={runStatusOptions}
          testID="activity-log-run-status-filter"
        />
        <Picker
          label={t("Last run result")}
          showActionBar
          selectionMode="multiple"
          selectedKeys={filters.resultStatuses}
          onSelectionChange={(keys) =>
            update({ resultStatuses: Array.from(keys).map(String) })
          }
          items={runResultOptions}
          testID="activity-log-run-result-filter"
        />
        {hasActiveFilters && (
          <GhostButton
            size="small"
            testID="activity-log-clear-filters"
            onPress={clearFilters}
          >
            {t("Clear filters")}
          </GhostButton>
        )}
      </div>
      <Search
        value={filters.search}
        testID="activity-log-search"
        aria-label={t("Search activity log")}
        placeholder={t("Search")}
        className={styles.search}
        onChange={(value) => update({ search: value })}
      />
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexShrink: 1,
  }),
  filters: css({
    display: "flex",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexWrap: "wrap",
    flexShrink: 1,
    flexGrow: 1,
    alignItems: "flex-start",
  }),
  search: css({
    display: "flex",
    flexShrink: 1,
    justifyContent: "flex-end",
    width: "100%",
    maxWidth: 276,
    flexBasis: 276,
  }),
};
